import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import { AsyncSelect, Form, FormField, ModalDialog, Numeric } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { SubstructureMaterialClass } from "../../../../http/deckDesigner/types/substructureMaterialClasses";
import { SupportFamily } from "../../../../http/deckDesigner/types/supportFamilies";
import { InsertSupportBody, Support, UpdateSupportBody } from "../../../../http/deckDesigner/types/supports";
import useDeckSubstructureMaterialClassesApi from "../../../../http/deckDesigner/useSubstructureMaterialClasses";
import useDeckSupportFamiliesApi from "../../../../http/deckDesigner/useSupportFamilies";
import useDeckSupportsApi from "../../../../http/deckDesigner/useSupports";
import useItemOptions, { getItemOptionLabel, ItemOption, toItemOption } from "../../../../hooks/useItemOptions";

interface SupportFormBody {
  item: ItemOption | null;
  supportFamily: SupportFamily | null;
  minHeight: number | null;
  maxHeight: number | null;
  stripeLength: number | null;
  width: number | null;
  materialClasses: SubstructureMaterialClass[];
}

interface SupportFormProps {
  support?: Support;
  onClose: () => void;
}

const SupportForm: FC<SupportFormProps> = ({ support, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const supportsApi = useDeckSupportsApi();
  const supportFamiliesApi = useDeckSupportFamiliesApi();
  const materialClassesApi = useDeckSubstructureMaterialClassesApi();
  const itemOptions = useItemOptions();
  const supportFamilyOptions = useAsyncOptions(supportFamiliesApi.getDeckSupportFamilies);
  const materialClassOptions = useAsyncOptions(materialClassesApi.getDeckSubstructureMaterialClasses);

  const defaultValues: SupportFormBody = {
    item: support ? toItemOption(support.item) : null,
    supportFamily: support?.supportFamily ?? null,
    minHeight: support?.minHeight ?? null,
    maxHeight: support?.maxHeight ?? null,
    stripeLength: support?.stripeLength ?? null,
    width: support?.width ?? null,
    materialClasses: support?.materialClasses ?? [],
  };

  const onSubmit = () => async (formData: SupportFormBody) => {
    if (
      !formData.item ||
      !formData.supportFamily ||
      formData.minHeight == null ||
      formData.maxHeight == null ||
      formData.width == null ||
      !formData.materialClasses.length
    ) {
      return;
    }

    const body: InsertSupportBody | UpdateSupportBody = {
      itemId: formData.item.id,
      supportFamilyCode: formData.supportFamily.code,
      minHeight: formData.minHeight,
      maxHeight: formData.maxHeight,
      stripeLength: formData.stripeLength,
      width: formData.width,
      materialClassIds: formData.materialClasses.map((materialClass) => materialClass.id),
    };

    try {
      if (support) {
        await supportsApi.updateDeckSupport(support.item.id, body);
      } else {
        await supportsApi.insertDeckSupport(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        support
          ? `${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.supports.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.deckDesignerModule.supports.singular}`
      }
    >
      <Form<SupportFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                  {({ labelId }) => (
                    <AsyncSelect<ItemOption, false>
                      {...field}
                      {...itemOptions}
                      inputId={labelId}
                      isMulti={false}
                      isDisabled={!!support}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={getItemOptionLabel}
                      getOptionValue={(option) => option.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="supportFamily"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.supportFamilies.singular}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<SupportFamily, false>
                      {...field}
                      {...supportFamilyOptions}
                      inputId={labelId}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="minHeight"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.minHeight} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="maxHeight"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.maxHeight} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="stripeLength"
              control={control}
              rules={{ min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.stripeLength} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="width"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.width} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="materialClasses"
              control={control}
              rules={{ validate: (value) => !!value.length }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructureMaterialClasses.plural}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<SubstructureMaterialClass, true>
                      {...materialClassOptions}
                      inputId={labelId}
                      value={field.value}
                      isMulti
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => `${option.name} - ${option.substructureMaterial.name}`}
                      getOptionValue={(option) => option.id}
                      isInvalid={fieldState.invalid}
                      onChange={(options) => field.onChange(Array.from(options))}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default SupportForm;
